import { Component, Input, EventEmitter, Output, DoCheck } from '@angular/core';
import { EquipmentType } from 'models';
import { Dictionary } from 'models';
import { DictionaryService } from '#services/api';
import { RegionService } from '#services/api/region.service';
import { Dictionaries } from 'enums/dictionary';

@Component({
  selector: 'app-equipment-type-template',
  templateUrl: './equipment-type-template.component.html',
  styleUrls: ['./equipment-type-template.component.scss'],
  standalone: false
})
export class EquipmentTypeTemplateComponent implements DoCheck {
  private _equipmentType: EquipmentType;

  @Input()
  set equipmentType(value: EquipmentType) {
    this._equipmentType = value;
    if (value) {
      this.regionInputValue = value.Region || '';
    }
  }
  get equipmentType(): EquipmentType {
    return this._equipmentType;
  }

  @Output() isValidChanged = new EventEmitter();

  orderTypes: Dictionary[];
  regions: Dictionary[];
  filteredRegions: Dictionary[] = [];
  regionInputValue: string = '';

  constructor(
    private dictionaryService: DictionaryService,
    private regionService: RegionService
  ) { }

  ngDoCheck(): void {
    this.isValidChanged.emit(!this.isInvalid);
  }

  get isInvalid(): boolean {
    if (!this.equipmentType) {
      return false;
    }

    return !this.equipmentType.Name || !this.equipmentType.OrderType;
  }

  get orderTypeDefValue(): Dictionary {
    return new Dictionary(this.equipmentType.OrderTypeID, this.equipmentType.OrderType);
  }

  async onLoadOrderTypes() {
    if (!this.orderTypes) {
      this.orderTypes = await this.dictionaryService.getAll(
        `${Dictionaries.orderTypes}/dictionary`).toPromise();
    }
  }

  async onLoadRegions() {
    if (!this.regions) {
      this.regions = await this.dictionaryService.getAll(
        `${Dictionaries.regions}/dictionary`).toPromise();
    }
    this.filteredRegions = this._filterRegions(this.regionInputValue);
  }

  onRegionInputChange(value: string) {
    this.regionInputValue = value;
    this.filteredRegions = this._filterRegions(value);
    // If user clears the field, unset the selection
    if (!value) {
      this.equipmentType.RegionID = null;
      this.equipmentType.Region = null;
    } else {
      // If the typed text no longer matches the current selection, clear the ID
      const match = (this.regions || []).find(r => r.Name.toLowerCase() === value.toLowerCase());
      if (!match) {
        this.equipmentType.RegionID = null;
        this.equipmentType.Region = null;
      }
    }
  }

  onRegionOptionSelected(event: any) {
    const name: string = event.option.value;
    const match = (this.regions || []).find(r => r.Name === name);
    if (match) {
      this.equipmentType.RegionID = match.ID;
      this.equipmentType.Region = match.Name;
    }
    this.regionInputValue = name;
  }

  async onRegionBlur() {
    const typed = (this.regionInputValue || '').trim();

    if (!typed) {
      this.equipmentType.RegionID = null;
      this.equipmentType.Region = null;
      return;
    }

    // Check if an existing region already matches
    const match = (this.regions || []).find(r => r.Name.toLowerCase() === typed.toLowerCase());
    if (match) {
      this.equipmentType.RegionID = match.ID;
      this.equipmentType.Region = match.Name;
      this.regionInputValue = match.Name;
      return;
    }

    // Create new region
    try {
      const created = await this.regionService.create({ ID: 0, Name: typed } as Dictionary).toPromise();
      if (!this.regions) {
        this.regions = [];
      }
      this.regions = [...this.regions, created];
      this.equipmentType.RegionID = created.ID;
      this.equipmentType.Region = created.Name;
      this.regionInputValue = created.Name;
    } catch (e) {
      console.error('Failed to create region', e);
    }
  }

  clearRegion() {
    this.equipmentType.RegionID = null;
    this.equipmentType.Region = null;
    this.regionInputValue = '';
    this.filteredRegions = this.regions || [];
  }

  private _filterRegions(value: string): Dictionary[] {
    if (!this.regions) { return []; }
    const lower = (value || '').toLowerCase();
    return this.regions.filter(r => r.Name.toLowerCase().includes(lower));
  }

  onOrderTypeChanged(event) {
    if (event.value) {
      this.equipmentType.OrderTypeID = (<Dictionary>event.value).ID;
      this.equipmentType.OrderType = (<Dictionary>event.value).Name;
    }
  }
}
